// src/rms/ads/avails.js
// ============================================================================
// Inventory forecasting — what we actually have to sell
// ============================================================================
// The pitch is never "500,000 impressions". It is "reach 412 verified boat
// rental operators, every working day, on the screen where they run their
// business". Volume is the wrong unit for a niche B2B audience; qualified reach
// is the right one, and it is why a marine insurer pays $18 CPM here and $2 on
// a general network.
//
// Everything below is derived from real operator and delivery data, never from
// a guess, so a number quoted to an advertiser can be defended.
// ============================================================================

import db from "../data";
import { AD_SLOTS, MASTHEAD_SLOTS, RAIL_SLOTS } from "./adInventory";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { getLocale } from "../i18n/locale";

/** Ad-serving slots per screen view: the rail plus both masthead units. */
const SLOTS_PER_VIEW = RAIL_SLOTS.length + MASTHEAD_SLOTS.length;

/**
 * Assumed working-day screen views per operator. Deliberately conservative —
 * over-promising avails is how a first IO becomes the last one.
 *
 * Replace with measured sessions once there is real traffic; the function takes
 * it as a parameter for exactly that reason.
 */
const DEFAULT_VIEWS_PER_OPERATOR_PER_DAY = 12;
const WORKING_DAYS_PER_MONTH = 22;

/**
 * Share of served impressions that actually meet the viewability threshold.
 * Measured from real data below where possible; this is only the fallback for a
 * vertical with no history yet.
 */
const ASSUMED_VIEWABILITY_RATE = 0.72;

/**
 * Sellable inventory per vertical.
 *
 * Returns one row per ad category with reachable operators, forecast monthly
 * viewable impressions, what is already committed, and what remains unsold.
 */
export async function computeAvails({
  viewsPerOperatorPerDay = DEFAULT_VIEWS_PER_OPERATOR_PER_DAY,
  now = new Date(),
} = {}) {
  const operators = (await db.list("operators", {})).data || [];
  const campaigns = (await db.list("adCampaigns", {})).data || [];
  const impressions = (await db.list("adImpressions", {})).data || [];
  const rateCard = (await db.list("adRateCard", {})).data || [];

  const categoryOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.key]: p.adCategory }), {});
  const labelOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.adCategory]: p.label }), {});

  // Only ad-supported operators are sellable. An ad-free subscriber is revenue,
  // but they are not inventory, and counting them would overstate every avail.
  const sellable = operators.filter((o) => o.ads_enabled && o.plan_tier !== "ad_free" && o.is_active !== false);

  const byCategory = {};
  sellable.forEach((o) => {
    const cat = categoryOf[o.vertical_key];
    if (!cat) return;
    byCategory[cat] = (byCategory[cat] || 0) + 1;
  });

  // Measured viewability, where there is enough data to mean anything.
  const totalImpressions = impressions.length;
  const viewableImpressions = impressions.filter((i) => i.is_viewable === true).length;
  const measuredRate = totalImpressions >= 100 ? viewableImpressions / totalImpressions : null;
  const viewabilityRate = measuredRate ?? ASSUMED_VIEWABILITY_RATE;

  const rows = Object.entries(byCategory).map(([adCategory, operatorCount]) => {
    const monthlyServed = operatorCount * viewsPerOperatorPerDay * WORKING_DAYS_PER_MONTH * SLOTS_PER_VIEW;
    const monthlyViewable = Math.round(monthlyServed * viewabilityRate);

    // Committed = what live flights still intend to deliver this month.
    const live = campaigns.filter(
      (c) =>
        (c.ad_categories || []).includes(adCategory) &&
        c.status === "live" &&
        (!c.ends_at || new Date(c.ends_at) > now)
    );
    const committed = live.reduce(
      (sum, c) => sum + Math.max(0, (c.goal_impressions || 0) - (c.delivered_impressions || 0)),
      0
    );

    const unsold = Math.max(0, monthlyViewable - committed);
    const fillPct = monthlyViewable ? Math.round((committed / monthlyViewable) * 100) : 0;

    const sidebarRate = rateCard.find((r) => r.ad_category === adCategory && r.placement === "sidebar");
    const bannerRate = rateCard.find((r) => r.ad_category === adCategory && r.placement === "banner_top");

    // Value of the unsold inventory at rack rate — the number that says which
    // vertical a salesperson should call today.
    const unsoldValueCents = Math.round((unsold / 1000) * (sidebarRate?.rack_cpm_cents || 0));

    return {
      adCategory,
      label: labelOf[adCategory] || adCategory,
      operators: operatorCount,
      monthlyServed,
      monthlyViewable,
      committed,
      unsold,
      fillPct,
      liveCampaigns: live.length,
      sidebarCpmCents: sidebarRate?.rack_cpm_cents || 0,
      bannerCpmCents: bannerRate?.rack_cpm_cents || 0,
      minSpendCents: sidebarRate?.min_spend_cents || 0,
      unsoldValueCents,
    };
  });

  return {
    viewabilityRate,
    viewabilityIsMeasured: measuredRate !== null,
    slotsPerView: SLOTS_PER_VIEW,
    viewsPerOperatorPerDay,
    workingDaysPerMonth: WORKING_DAYS_PER_MONTH,
    totalSellableOperators: sellable.length,
    rows: rows.sort((a, b) => b.unsoldValueCents - a.unsoldValueCents),
  };
}

/**
 * Can this vertical actually deliver what an advertiser wants to buy?
 *
 * Answering honestly at quote time avoids the worst outcome in ad sales:
 * accepting an IO that the inventory cannot fill, then under-delivering.
 */
export function canDeliver(availRow, requestedImpressions) {
  if (!availRow) return { ok: false, reason: "No operators in this vertical yet." };
  if (requestedImpressions > availRow.unsold) {
    return {
      ok: false,
      reason: `Only ${availRow.unsold.toLocaleString(getLocale())} viewable impressions remain unsold this month.`,
      maxDeliverable: availRow.unsold,
    };
  }
  return { ok: true, maxDeliverable: availRow.unsold };
}

/** Placement inventory, for the media kit. */
export function placementSpecs() {
  return Object.values(AD_SLOTS)
    .filter((s) => s.adsAllowed)
    .map((s) => ({
      key: s.key,
      label: s.label,
      placement: s.placement,
      size: `${s.width}×${s.height}`,
      rotationSeconds: s.rotationSeconds,
    }));
}

const avails = { computeAvails, canDeliver, placementSpecs };
export default avails;
